import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  PayrollPaymentListItem,
  PayrollPaymentListResponse,
  PayrollPaymentVm,
  GetPaymentAmountResponse,
  GetPaymentAmountEmployeeItem,
  PayrollPaymentLineItem,
  PayrollPaymentLinesResponse,
  PayrollPaymentSavePayload,
  IsAnyPaymentRemainingResponse,
  PayrollPaymentSummary,
  PayrollPaymentSummaryEmployee,
  PayrollPaymentSummaryTotals,
} from '@/api/types/payroll.types'
import type { ApiResponse } from '@/api/types'

/**
 * Convert YYYY-MM (form) to MM-YYYY for PayrollPayment API (e.g. 2026-02 -> 02-2026)
 */
function num(value: unknown, fallback = 0): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function mapPaymentHeader(item: any): PayrollPaymentListItem {
  const dateVal = item.date ?? item.Date ?? item.paymentDate ?? item.PaymentDate ?? ''
  return {
    id: item.id ?? item.Id,
    paymentNo: item.paymentNo ?? item.PaymentNo ?? '',
    payrollMonthYear: item.payrollMonthYear ?? item.PayrollMonthYear,
    date: dateVal,
    paymentDate: dateVal,
    amount: item.amount ?? item.Amount,
    branchId: item.branchId ?? item.BranchId,
    departmentId: item.departmentId ?? item.DepartmentId,
    isPosted: item.isPosted ?? item.IsPosted,
    creditAccount: item.creditAccount ?? item.CreditAccount,
    creditAccountName: item.creditAccountName ?? item.CreditAccountName,
  }
}

function mapSummaryEmployee(item: any): PayrollPaymentSummaryEmployee {
  return {
    lineId: item.lineId ?? item.LineId,
    salaryId: num(item.salaryId ?? item.SalaryId),
    empId: num(item.empId ?? item.EmpId),
    empCode: item.empCode ?? item.EmpCode ?? '',
    employeeName: item.employeeName ?? item.EmployeeName ?? '',
    employeeNameAr: item.employeeNameAr ?? item.EmployeeNameAr ?? '',
    basicSalary: num(item.basicSalary ?? item.BasicSalary),
    housingAllowance: num(item.housingAllowance ?? item.HousingAllowance),
    transportationAllowance: num(item.transportationAllowance ?? item.TransportationAllowance),
    mealAllowance: num(item.mealAllowance ?? item.MealAllowance),
    grossPay: num(item.grossPay ?? item.GrossPay),
    allowances: num(item.allowances ?? item.Allowances),
    bonus: num(item.bonus ?? item.Bonus),
    overtime: num(item.overtime ?? item.Overtime),
    deductions: num(item.deductions ?? item.Deductions),
    taxes: num(item.taxes ?? item.Taxes),
    arrears: num(item.arrears ?? item.Arrears),
    netPay: num(item.netPay ?? item.NetPay),
    amountPaid: num(item.amountPaid ?? item.AmountPaid),
    remainingAmount: num(item.remainingAmount ?? item.RemainingAmount),
    gosiDeduction: num(item.gosiDeduction ?? item.GosiDeduction),
    loanDeduction: num(item.loanDeduction ?? item.LoanDeduction),
    absentDeduction: num(item.absentDeduction ?? item.AbsentDeduction),
    lateDeduction: num(item.lateDeduction ?? item.LateDeduction),
    earlyOutDeduction: num(item.earlyOutDeduction ?? item.EarlyOutDeduction),
    otherDeductions: num(item.otherDeductions ?? item.OtherDeductions),
    taxDeduction: num(item.taxDeduction ?? item.TaxDeduction),
    familyTaxMonthly: num(item.familyTaxMonthly ?? item.FamilyTaxMonthly),
    otherTax: num(item.otherTax ?? item.OtherTax),
  }
}

function mapSummaryTotals(item: any): PayrollPaymentSummaryTotals {
  return {
    payrollCost: num(item.payrollCost ?? item.PayrollCost),
    netPay: num(item.netPay ?? item.NetPay),
    amountPaid: num(item.amountPaid ?? item.AmountPaid),
    remainingAmount: num(item.remainingAmount ?? item.RemainingAmount),
    taxes: num(item.taxes ?? item.Taxes),
    deductions: num(item.deductions ?? item.Deductions),
    allowances: num(item.allowances ?? item.Allowances),
    bonus: num(item.bonus ?? item.Bonus),
    overtime: num(item.overtime ?? item.Overtime),
    arrears: num(item.arrears ?? item.Arrears),
    gosiDeduction: num(item.gosiDeduction ?? item.GosiDeduction),
    loanDeduction: num(item.loanDeduction ?? item.LoanDeduction),
    absentDeduction: num(item.absentDeduction ?? item.AbsentDeduction),
    lateDeduction: num(item.lateDeduction ?? item.LateDeduction),
    earlyOutDeduction: num(item.earlyOutDeduction ?? item.EarlyOutDeduction),
    otherDeductions: num(item.otherDeductions ?? item.OtherDeductions),
    taxDeduction: num(item.taxDeduction ?? item.TaxDeduction),
    familyTaxMonthly: num(item.familyTaxMonthly ?? item.FamilyTaxMonthly),
    otherTax: num(item.otherTax ?? item.OtherTax),
  }
}

function mapPaymentLine(item: any): PayrollPaymentLineItem {
  const amount = Number(item.amount ?? item.Amount ?? 0)
  const remainingAmount = Number(item.remainingAmount ?? item.RemainingAmount ?? 0)
  const payrollRemainingAmount = Number(
    item.payrollRemainingAmount ?? item.PayrollRemainingAmount ?? amount + remainingAmount,
  )
  return {
    id: item.id ?? item.Id,
    paymentId: item.paymentId ?? item.PaymentId ?? item.paymentID ?? item.PaymentID,
    empId: item.empId ?? item.EmpId ?? 0,
    amount,
    remainingAmount,
    payrollRemainingAmount,
    branchId: item.branchId ?? item.BranchId,
  }
}

export function toPayrollPaymentMonthYear(ym: string): string {
  if (!ym || ym.length < 7) return ym
  const [y, m] = ym.split('-')
  return `${m}-${y}`
}

export const payrollPaymentService = {
  getAll(
    branchId: number,
    departmentId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<PayrollPaymentListResponse> {
    const url = endpoints.payrollPayment.getAll(branchId, departmentId, pageNumber, pageSize)
    return apiClient.get<ApiResponse<PayrollPaymentListResponse> | PayrollPaymentListResponse>(url).then((res) => {
      const raw = res.data as any
      const arr = raw?.data ?? (Array.isArray(raw) ? raw : [])
      const list = (Array.isArray(arr) ? arr : []).map((item: any): PayrollPaymentListItem => mapPaymentHeader(item))
      return {
        data: list,
        totalRecords: raw?.totalRecords ?? list.length,
        pageNumber: raw?.pageNumber ?? pageNumber,
        pageSize: raw?.pageSize ?? pageSize,
      }
    })
  },

  getById(id: number, branchId: number): Promise<{
    payrollMonthYear: string
    date: string
    amount: number
    branchId: number
    isPosted?: boolean
  }> {
    const url = endpoints.payrollPayment.getById(id, branchId)
    return apiClient.get(url).then((res) => {
      const raw = res.data as any
      if (!raw || typeof raw !== 'object') return raw
      const dateVal = raw.date ?? raw.Date ?? raw.paymentDate ?? raw.PaymentDate ?? ''
      return {
        id: raw.id ?? raw.Id,
        payrollMonthYear: raw.payrollMonthYear ?? raw.PayrollMonthYear ?? '',
        paymentDate: dateVal,
        date: dateVal,
        amount: raw.amount ?? raw.Amount ?? 0,
        branchId: raw.branchId ?? raw.BranchId ?? 0,
        isPosted: raw.isPosted ?? raw.IsPosted,
      }
    })
  },

  getLinesByPaymentId(id: number, branchId: number): Promise<PayrollPaymentLineItem[]> {
    const url = endpoints.payrollPayment.getLinesByPaymentId(id, branchId)
    return apiClient.get(url).then((res) => {
      const raw = res.data as any
      const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
      if (!Array.isArray(arr)) return []
      return arr.map((item: any): PayrollPaymentLineItem => mapPaymentLine(item))
    })
  },

  getLinesByPaymentIdWithPaging(
    paymentId: number,
    branchId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<PayrollPaymentLinesResponse> {
    const url = endpoints.payrollPayment.getLinesByPaymentIdWithPaging(paymentId, branchId, pageNumber, pageSize)
    return apiClient.get<ApiResponse<any> | any>(url).then((res) => {
      const raw = res.data as any
      const payload = raw?.data ?? raw
      const arr = payload?.data ?? (Array.isArray(payload) ? payload : [])
      const list = (Array.isArray(arr) ? arr : []).map((item: any): PayrollPaymentLineItem => mapPaymentLine(item))
      return {
        data: list,
        totalRecords: payload?.totalRecords ?? raw?.totalRecords ?? list.length,
        pageNumber: payload?.pageNumber ?? raw?.pageNumber ?? pageNumber,
        pageSize: payload?.pageSize ?? raw?.pageSize ?? pageSize,
      }
    })
  },

  getByMonthYear(monthYear: string, branchId: number): Promise<PayrollPaymentListItem[]> {
    const apiMonthYear = toPayrollPaymentMonthYear(monthYear)
    const url = endpoints.payrollPayment.getByMonthYear(apiMonthYear, branchId)
    return apiClient.get(url).then((res) => {
      const raw = res.data as any
      const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
      if (!Array.isArray(arr)) return []
      return arr.map((item: any): PayrollPaymentListItem => mapPaymentHeader(item))
    })
  },

  getSummary(paymentId: number, branchId: number): Promise<PayrollPaymentSummary | null> {
    const url = endpoints.payrollPayment.getSummary(paymentId, branchId)
    return apiClient.get(url).then((res) => {
      const raw = res.data as any
      if (!raw || typeof raw !== 'object') return null
      const dateVal = raw.date ?? raw.Date ?? raw.paymentDate ?? raw.PaymentDate ?? ''
      const totalsRaw = raw.totals ?? raw.Totals ?? {}
      const employeesRaw = raw.employees ?? raw.Employees ?? []
      return {
        id: Number(raw.id ?? raw.Id ?? 0),
        paymentNo: raw.paymentNo ?? raw.PaymentNo ?? '',
        payrollMonthYear: raw.payrollMonthYear ?? raw.PayrollMonthYear ?? '',
        date: dateVal,
        paymentDate: dateVal,
        amount: num(raw.amount ?? raw.Amount),
        branchId: raw.branchId ?? raw.BranchId,
        isPosted: raw.isPosted ?? raw.IsPosted,
        employeeCount: num(raw.employeeCount ?? raw.EmployeeCount),
        totals: mapSummaryTotals(totalsRaw),
        employees: (Array.isArray(employeesRaw) ? employeesRaw : []).map(mapSummaryEmployee),
      }
    })
  },

  getPaymentAmount(monthYear: string, departmentId: number, branchId: number): Promise<GetPaymentAmountResponse> {
    const apiMonthYear = toPayrollPaymentMonthYear(monthYear)
    const url = endpoints.payrollPayment.getPaymentAmount(apiMonthYear, departmentId, branchId)
    return apiClient.get<GetPaymentAmountResponse>(url).then((res) => {
      const raw = res.data as any
      const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
      if (!Array.isArray(arr)) return []
      return arr.map((item: any): GetPaymentAmountEmployeeItem => ({
        employeeId: item.employeeId ?? item.EmployeeId ?? item.employerId ?? item.EmployerId ?? 0,
        empName: item.empName ?? item.EmpName ?? item.employeeName ?? item.EmployeeName ?? '—',
        totalAmount: item.totalAmount ?? item.TotalAmount ?? 0,
        remainingAmount: item.remainingAmount ?? item.RemainingAmount ?? 0,
      }))
    })
  },

  /**
   * Swagger: POST /api/PayrollPayment/Save
   * Body:
   * { payrollMonthYear, paymentDate, branchId, departmentId }
   */
  save(payload: PayrollPaymentSavePayload): Promise<unknown> {
    return apiClient.post(endpoints.payrollPayment.save, payload).then((r) => r.data)
  },

  isAnyPaymentRemaining(branchId: number, departmentId: number, monthYear: string): Promise<IsAnyPaymentRemainingResponse> {
    // Swagger expects MM-YYYY (e.g. 04-2026) in the URL path
    const apiMonthYear = toPayrollPaymentMonthYear(monthYear)
    const url = endpoints.payrollPayment.isAnyPaymentRemaining(branchId, departmentId, apiMonthYear)
    return apiClient.get<IsAnyPaymentRemainingResponse>(url).then((r) => r.data as any)
  },

  create(payload: PayrollPaymentVm): Promise<unknown> {
    return apiClient.post(endpoints.payrollPayment.create, payload).then((r) => r.data)
  },

  update(payload: PayrollPaymentVm): Promise<unknown> {
    return apiClient.put(endpoints.payrollPayment.update, payload).then((r) => r.data)
  },

  updateByIdBranch(
    id: number,
    branchId: number,
    payload: {
      paymentDate: string
      payrollPaymentDetails: Array<{
        id?: number
        employeeId: number
        amount: number
        remainingAmount: number
      }>
    },
  ): Promise<unknown> {
    const url = endpoints.payrollPayment.updateByIdBranch(id, branchId)
    return apiClient.put(url, payload).then((r) => r.data)
  },

  delete(id: number, branchId: number): Promise<unknown> {
    const url = endpoints.payrollPayment.delete(id, branchId)
    return apiClient.delete(url).then((r) => r.data)
  },

  postData(id: number, branchId: number): Promise<unknown> {
    const url = endpoints.payrollPayment.postData(id, branchId)
    return apiClient.post(url).then((r) => r.data)
  },

  refresh(id: number, branchId: number): Promise<unknown> {
    const url = endpoints.payrollPayment.refresh(id, branchId)
    return apiClient.post(url).then((r) => r.data)
  },
}
